/* Peachy Loop bundle minting — SINGLE SOURCE OF TRUTH.
 *
 * Every subscription bundle line item's `_bundleId` MUST be a freshly
 * minted, UNIQUE Loop transaction id (txnId). It must NEVER be a static or
 * hardcoded value: reusing one id made two separate subscriptions share a
 * single Loop bundle, so editing one silently edited the other.
 *
 * Anything that writes `_bundleId` — the buy box add flow, the cart re-tier
 * (peachy-cart-bundle-lock.js) and the cart upsell (peachy-cart-flavor-upsell.js)
 * — goes through this one function. There is no other way to obtain a
 * `_bundleId`, so a static value can never re-enter the system.
 *
 *   window.PeachyLoopMint.mint({ productVariants, total, sellingPlanShopifyId })
 *     -> Promise<txnId>
 *
 * The promise REJECTS on any failure. Callers MUST abort the operation on
 * rejection and never fall back to a reused/blank id.
 */
(function () {
  'use strict';

  var LOOP_API = 'https://api.loopsubscriptions.com/storefront/2023-10';

  // Stable Loop identifiers (bundle config id + box size id) per bag tier.
  // These are NOT per-order tickets — they identify the BYOB bundle config
  // and box size, and are reused on every mint. Verified against Loop's
  // live API on 2026-05-29 via
  //   GET /storefront/2023-10/bundle?myshopifyDomain=peachycleanstore.myshopify.com
  // If the Loop bundle lineup changes (e.g. a new flavor launch alters the
  // BYOB config), update it HERE — this is the only copy in the theme.
  var CONFIG = {
    2: { bundleId: '01KQERJ16SPAN2VWYSK096GRWV', boxSizeId: '01KQERJ16VXCQWMNNJ9T34EK3M' },
    3: { bundleId: '01KQEZY960ZA3S7654K555KPG3', boxSizeId: '01KQEZY962SAYE8YC63M65Q3X6' },
    4: { bundleId: '01KQEZZQ4V1D2D2XS9EX2129A7', boxSizeId: '01KQRYDWZNFEY87H6RCF3QB1NG' } // RANGE 4–10
  };

  // Offer-test bundles, keyed by SELLING PLAN id — checked before the
  // per-total map so the [TEST] plan always mints against its own Loop
  // bundle no matter which page or re-tier path triggers the mint.
  // ([TEST] Bundle Builder — 3 Bag (buy 2 get 1 free), verified against
  // Loop's live API on 2026-07-13.)
  var PLAN_CONFIG = {
    4847763679: { bundleId: '01KXDADXS6W94J852M2JCWT6S5', boxSizeId: '01KXDADXS77TFW1XAW1EH8S95P' }
  };

  // Bags 4–10 all route through the single RANGE bundle.
  function configForTotal(total) {
    if (total >= 4) return CONFIG[4];
    return CONFIG[total] || null;
  }

  function configFor(opts) {
    var planId = parseInt(opts.sellingPlanShopifyId, 10);
    if (planId && PLAN_CONFIG[planId]) return PLAN_CONFIG[planId];
    return configForTotal(opts.total);
  }

  // Mint a fresh Loop transaction for the given bundle and resolve with its
  // txnId. Rejects on any failure so the caller blocks rather than ever
  // falling back to a reused id.
  function mint(opts) {
    opts = opts || {};
    var total = opts.total;
    var cfg = configFor(opts);
    if (!cfg) return Promise.reject(new Error('No Loop bundle configured for ' + total + ' bags.'));

    var productVariants = opts.productVariants || [];
    if (!productVariants.length) return Promise.reject(new Error('No product variants to mint a Loop transaction.'));

    var body = {
      boxSizeId: cfg.boxSizeId,
      sellingPlanShopifyId: opts.sellingPlanShopifyId || null,
      productVariants: productVariants,
      quantity: 1
    };
    // True only when the request never got an answer from Loop at all
    // (fetch rejected: "Load failed" / "Failed to fetch"). An answer from
    // Loop, even an error, is final and never retried via the proxy.
    var unreachable = false;
    return fetch(LOOP_API + '/bundle/' + cfg.bundleId + '/transaction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(null, function (netErr) { unreachable = true; throw netErr; })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
      .then(txnFrom)
      .catch(function (err) {
        if (!unreachable) throw err;
        return mintViaProxy(cfg.bundleId, body).then(
          function (txnId) {
            report('ok');
            return txnId;
          },
          function (proxyErr) {
            report('fail', proxyErr && proxyErr.message);
            throw err; // the caller handles the ORIGINAL failure, as before
          }
        );
      });
  }

  function txnFrom(res) {
    if (!res.ok || !res.json || !res.json.success || !res.json.data || !res.json.data.txnId) {
      throw new Error((res.json && res.json.message) || 'Loop transaction failed.');
    }
    return res.json.data.txnId;
  }

  // Fallback (2026-10-06): the storefront error log showed ~12 shoppers in
  // a week whose phones never reach api.loopsubscriptions.com (blocked on
  // the device or network) yet do reach our vercel.app host, so they could
  // not buy any 2+ bag subscription. Only after the direct call fails to
  // connect, our server (ops-api/api/loop-mint.js) makes the same public
  // Loop call and returns the txnId. Still a fresh, unique ticket per
  // call; text/plain body so the browser sends no preflight.
  var PROXY = 'https://peachy-ops-api.vercel.app/api/loop-mint';
  function mintViaProxy(bundleId, body) {
    var payload = {
      bundleId: bundleId,
      boxSizeId: body.boxSizeId,
      sellingPlanShopifyId: body.sellingPlanShopifyId,
      productVariants: body.productVariants
    };
    return fetch(PROXY, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
      .then(txnFrom);
  }

  // Log how the fallback did, so its effect is measured, not assumed.
  function report(stage, message) {
    try {
      if (window.PeachyReportError) window.PeachyReportError('mint_proxy', { stage: stage, message: message });
    } catch (e) { /* never affect the mint */ }
  }

  window.PeachyLoopMint = { mint: mint, configForTotal: configForTotal };
})();
